import { useState, useEffect } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { ArrowRight, Eye, EyeOff, CheckCircle } from 'lucide-react'
import axiosInstance from '../../services/axiosInstance'
import './styles.scss'

export default function ResetPassword() {
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(false)
  
  const navigate = useNavigate()
  const location = useLocation()
  
  // Extract token from query params
  const searchParams = new URLSearchParams(location.search)
  const token = searchParams.get('token')

  useEffect(() => {
    if (!token) {
      setError('Invalid or missing password reset token. Please request a new link.')
    }
  }, [token])

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.')
      return
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long.')
      return
    }

    setLoading(true)
    setError(null)
    setSuccess(false)

    try {
      const response = await axiosInstance.post('/api/auth/reset-password', { token, newPassword })
      if (response.data.success) {
        setSuccess(true)
        setTimeout(() => {
          navigate('/login', { state: { message: 'Password has been successfully reset. Please log in.' } })
        }, 3000)
      } else {
        setError(response.data.message || 'Failed to reset password.')
      }
    } catch (err) {
      console.error(err)
      setError(err.response?.data?.message || 'An unexpected error occurred. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="auth-page">
      <div className="auth-brand">
        <div className="auth-brand__dots" />
        <div className="auth-brand__content">
          <div className="auth-brand__logo">
            <img src="/campusready_logo.svg" alt="CampusReady" />
            <span>CampusReady<span className="ai">.ai</span></span>
          </div>
          <h1>Create New Password</h1>
          <p>
            Please enter your new password below. Make sure it's at least 8 characters long.
          </p>
        </div>
      </div>

      <div className="auth-form-panel">
        <div className="auth-form-container">
          <div className="auth-form-header">
            <h2>Reset Password</h2>
            <p>Enter your new password.</p>
          </div>

          {error && (
            <div className="auth-error">
              <span>{error}</span>
            </div>
          )}

          {success ? (
            <div className="auth-form">
              <div style={{ textAlign: 'center', padding: '2rem 1rem' }}>
                <CheckCircle size={48} color="#10B981" style={{ marginBottom: '1rem' }} />
                <h3 style={{ marginBottom: '0.5rem', color: '#0F172A' }}>Password Reset Successful</h3>
                <p style={{ color: '#64748B', lineHeight: '1.5' }}>
                  Your password has been securely updated. Redirecting you to sign in...
                </p>
              </div>
            </div>
          ) : (
            <form className="auth-form" onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="newPassword">New Password</label>
                <div className="password-wrap">
                  <input
                    id="newPassword"
                    type={showPassword ? 'text' : 'password'}
                    name="newPassword"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                    required
                    disabled={!token}
                  />
                  <button
                    type="button"
                    className="eye-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="confirmPassword">Confirm Password</label>
                <div className="password-wrap">
                  <input
                    id="confirmPassword"
                    type={showPassword ? 'text' : 'password'}
                    name="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                    required
                    disabled={!token}
                  />
                </div>
              </div>

              <button type="submit" className="auth-submit" disabled={loading || !token || !newPassword || !confirmPassword}>
                {loading ? (
                  <span className="spinner" />
                ) : (
                  <>Reset Password <ArrowRight size={16} /></>
                )}
              </button>
            </form>
          )}
          
          <div style={{ marginTop: '2rem', textAlign: 'center' }}>
            <Link to="/login" style={{ color: '#64748B', textDecoration: 'none', fontSize: '0.9rem', fontWeight: '500' }}>
              Back to Sign In
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}
